import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkMutation } from '@platform/state/api';
import { Button, Dialog, ErrorBanner } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useAppDispatch, useAppSelector } from '@platform/state/hooks';
import { selectDialog } from '@platform/state/selectors';
import { dialogClosed, selectionCleared, toastShown } from '@platform/state/slices/uiSlice';
import type { BulkAction } from '@platform/core/types';
import type { IBulkDialogProps } from './IBulkDialogProps';

/** Масові дії над вибраними записами: статус, виконавець, видалення. */
export function BulkDialog({ model }: IBulkDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const strings = useModuleStrings();
  const dialog = useAppSelector(selectDialog);
  const [action, setAction] = useState<BulkAction>('status');
  const [value, setValue] = useState('');
  const [bulk, { isLoading, error }] = useBulkMutation();
  if (dialog.kind !== 'bulk' || dialog.recordType !== model.rt.key) return null;
  const close = () => dispatch(dialogClosed());
  const run = async () => {
    const result = await bulk({ ws: model.ws, rt: model.rt.key, body: { ids: model.selection, action, payload: { value } } }).unwrap();
    dispatch(toastShown({ kind: Object.keys(result.failed).length ? 'error' : 'success', text: `OK: ${result.succeeded}, failed: ${Object.keys(result.failed).length}` }));
    dispatch(selectionCleared(model.rt.key));
    close();
  };
  return (
    <Dialog title={t('list.selected', { count: model.selection.length })} onClose={close}
      footer={<><Button onClick={close}>{t('app.cancel')}</Button><Button variant={action === 'delete' ? 'danger' : 'primary'} disabled={isLoading} onClick={run}>{t('app.save')}</Button></>}>
      <label className="field"><span className="field__label">{t('app.actions')}</span>
        <select className="control" value={action} onChange={(e) => setAction(e.target.value as BulkAction)}>
          <option value="status">{t('list.bulk.status')}</option>
          <option value="delete">{t('list.bulk.delete')}</option>
        </select>
      </label>
      {action === 'status' && (
        <label className="field"><span className="field__label">{t('form.status')}</span>
          <select className="control" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">—</option>
            {model.rt.statuses.list.map((s) => <option key={s.value} value={s.value}>{strings.status(model.rt.key, s.value, s.label)}</option>)}
          </select>
        </label>
      )}
      <ErrorBanner error={error} />
    </Dialog>
  );
}
